import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReceiptText } from 'lucide-react';
import { getPaymentOptions, clearPaymentOptions } from '../../redux/paymentSlice';

// Phase-1 is offline-only: cash is collected in person; card/online are recorded.
// LAST-RESORT fallback, used ONLY when the /payment/options REQUEST FAILED (the
// payment branch isn't deployed on this environment, the network dropped) so the
// booking step asks something instead of going blank. It must NEVER stand in for
// a successful answer that carried no methods: an empty list is a decision the
// server made — the hospital disabled the method, the doctor accepts none, Super
// Admin put one on hold, or the plan has no `online_payment` — and offering Cash
// at a hospital where cash is off writes a payment record the doctor never agreed
// to. supportedTiming mirrors the backend PaymentSetting defaults. There is no
// "pay later" — every appointment must be settled before it can be completed.
const DEFAULT_METHODS = [
  { key: 'cash', label: 'Cash', requiresGateway: false, supportedTiming: 'postpaid' },
  { key: 'card', label: 'Card / Terminal', requiresGateway: false, supportedTiming: 'both' },
  { key: 'online', label: 'Online', requiresGateway: false, supportedTiming: 'both' },
];

const TYPE_OPTIONS = [
  { key: 'postpaid', label: 'After the visit' },
  { key: 'prepaid', label: 'Before the visit' },
];

// In-modal payment STEP rendered as a mini-INVOICE: the appointment details sit on
// the left, the payment method/timing/status on the right. Enforces the rule that a
// prepaid (online) booking can never be cash. Emits { method, timing } via onChange.
// `summary` is an array of { label, value } describing the appointment.
export default function PaymentMethodSelect({ doctorId, value, onChange, summary = [] }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentOptions = useSelector((state) => state.payment?.paymentOptions);
  // 'idle' | 'loading' | 'succeeded' | 'failed' — the difference between "we
  // haven't heard back" and "the server answered, and the answer was none".
  const optionsStatus = useSelector((state) => state.payment?.paymentOptionsStatus || 'idle');

  const [type, setType] = useState(value?.timing || 'postpaid');

  useEffect(() => {
    // Clear on the way out of a doctor: the resolved options are global state,
    // so without this the NEXT doctor is briefly judged on the previous one's
    // answer — and "accepts nothing" now blocks a booking.
    if (doctorId) dispatch(getPaymentOptions(doctorId));
    else dispatch(clearPaymentOptions());
  }, [doctorId, dispatch]);

  // Offline methods available regardless of timing.
  // Drop the retired 'paylater' method if a stale backend config still returns it.
  const fromApi = (paymentOptions?.methods || []).filter((m) => !m.requiresGateway && m.key !== 'paylater');
  const optionsPending = !doctorId || optionsStatus === 'idle' || optionsStatus === 'loading';
  // The server ANSWERED and the answer was "none". Not a deployment gap: a rule.
  const noneAccepted = !optionsPending && optionsStatus === 'succeeded' && fromApi.length === 0;
  const emptyReason = paymentOptions?.emptyReason || null;
  // Fall back to the built-in list only on a FAILED request (see DEFAULT_METHODS).
  const allMethods = fromApi.length ? fromApi : (optionsStatus === 'failed' && doctorId ? DEFAULT_METHODS : []);
  // WHY the list is empty, in the server's own words. /payment/options computes
  // the admin-enabled INTERSECT doctor-accepted INTERSECT not-held INTERSECT plan
  // set, so it is the only thing that knows which of those four emptied it, and
  // it now says so. This used to read `heldMethods` instead — every active hold
  // for the tenant, whether or not it touched a method this doctor accepts — so
  // a doctor who simply accepts nothing was told to wait for a hold to lift,
  // which would never happen.

  // 'postpaid' | 'prepaid' | 'both' — when 'both' the patient/doctor chooses.
  const policy = paymentOptions?.timing || 'postpaid';
  const method = value?.method || '';
  const activeTiming = policy === 'both' ? type : policy;

  // Methods valid for a given timing: prepaid (online) excludes at-clinic-only
  // methods (cash, pay-later), postpaid accepts everything offline.
  const methodsFor = (timing) =>
    allMethods.filter((m) => {
      const st = m.supportedTiming || 'postpaid';
      return st === 'both' || st === timing;
    });

  const methods = methodsFor(activeTiming);

  // Keep a valid method selected: auto-select the first valid one (so every
  // booking carries a payment record), or clear it when none fit the timing.
  // `unavailable` is the flag the booking form checks: it separates "nothing
  // chosen yet" from "the server says this doctor accepts nothing", which must
  // block the booking rather than record a payment nobody agreed to.
  useEffect(() => {
    const keys = methods.map((m) => m.key);
    if (methods.length && (!method || !keys.includes(method))) {
      onChange?.({ method: methods[0].key, timing: activeTiming, unavailable: false });
    } else if (!methods.length && (method || value?.unavailable !== noneAccepted)) {
      onChange?.({ method: '', timing: activeTiming, unavailable: noneAccepted });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTiming, method, paymentOptions, optionsStatus]);

  const pickMethod = (key) => onChange?.({ method: key, timing: activeTiming, unavailable: false });

  const pickType = (key) => {
    setType(key);
    // Re-resolve the method under the new timing (policy is 'both' here so the
    // new active timing is exactly `key`). Drop cash if switching to prepaid.
    const next = methodsFor(key);
    const keep = next.find((m) => m.key === method);
    onChange?.({
      method: keep ? method : (next[0]?.key || ''),
      timing: key,
      unavailable: next.length ? false : noneAccepted,
    });
  };

  const hasSummary = Array.isArray(summary) && summary.length > 0;

  return (
    <div data-testid="payment-method-step" className="rounded-2xl border border-gray-200 overflow-hidden">
      {/* Invoice header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5">
        <ReceiptText className="w-4 h-4 text-[#4ABDBD]" />
        <h3 className="text-sm font-semibold text-gray-800">
          {t('appointmentmodal.invoice_payment', 'Invoice & payment')}
        </h3>
        <span data-testid="payment-status-pill" className="ml-auto inline-flex items-center text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
          {t('payments.not_paid', 'Not paid')}
        </span>
      </div>

      {/* Appointment details | Payment — laid out horizontally */}
      <div className={`grid grid-cols-1 ${hasSummary ? 'sm:grid-cols-2' : ''} divide-y sm:divide-y-0 sm:divide-x divide-gray-100`}>
        {hasSummary && (
          <div className="p-4 space-y-2" data-testid="payment-invoice-summary">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
              {t('appointmentmodal.appointment_details', 'Appointment details')}
            </p>
            {summary.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500">{row.label}</span>
                <span className="font-semibold text-gray-700 text-right break-words">{row.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="p-4 space-y-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
            {t('appointmentmodal.payment_method', 'Payment method')}
          </p>

          {policy === 'both' && (
            <div data-testid="payment-type-options">
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                {t('appointmentmodal.payment_type', 'When to pay')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => pickType(o.key)}
                    data-testid={`payment-type-${o.key}`}
                    className={`text-sm px-3 py-2 rounded-xl border transition ${
                      activeTiming === o.key
                        ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold'
                        : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'
                    }`}
                  >
                    {t(`appointmentmodal.payment_type_${o.key}`, o.label)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {methods.length > 0 ? (
            <div data-testid="payment-method-options" role="radiogroup" aria-label={t('appointmentmodal.payment_method', 'Payment method')}>
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                {t('payments.method', 'Method')}
              </p>
              {/* Methods are a single-select radio list — visually distinct from the
                  pill-style "When to pay" timing buttons above. */}
              <div className="flex flex-wrap gap-2">
                {methods.map((m) => {
                  const selected = method === m.key;
                  return (
                    <label
                      key={m.key}
                      data-testid={`payment-method-${m.key}`}
                      className={`flex items-center w-fit gap-3 px-3.5 py-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selected
                          ? 'border-[#4ABDBD] bg-[#4ABDBD]/5 ring-1 ring-[#4ABDBD]/30 shadow-sm'
                          : 'border-gray-200 hover:border-[#4ABDBD]/60 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value={m.key}
                        checked={selected}
                        onChange={() => pickMethod(m.key)}
                        className="sr-only"
                      />
                      {/* Custom radio dial */}
                      <span
                        aria-hidden="true"
                        className={`flex items-center justify-center h-5 w-5 rounded-full border-2 transition-colors duration-200 ${
                          selected ? 'border-[#4ABDBD]' : 'border-gray-300'
                        }`}
                      >
                        <span
                          className={`h-2.5 w-2.5 rounded-full bg-[#4ABDBD] transition-transform duration-200 ${
                            selected ? 'scale-100' : 'scale-0'
                          }`}
                        />
                      </span>
                      <span className={`text-sm ${selected ? 'text-[#3AA8A8] font-semibold' : 'text-gray-700 font-medium'}`}>
                        {m.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : optionsPending ? (
            <p data-testid="payment-methods-loading" className="text-xs text-gray-400">
              {t('appointmentmodal.loading_payment_methods', 'Checking which payment methods this doctor accepts…')}
            </p>
          ) : noneAccepted ? (
            <p data-testid="payment-no-methods-accepted" className="text-xs text-red-600">
              {emptyReason === 'method_on_hold'
                ? t('appointmentmodal.no_method_all_on_hold', 'Every payment method this doctor accepts is temporarily on hold, so the appointment cannot be booked right now. Try again once the hold is lifted.')
                : emptyReason === 'hospital_disabled_accepted_methods'
                  ? t('appointmentmodal.no_method_hospital_disabled', 'The hospital has switched off every payment method this doctor accepts, so the appointment cannot be booked. Enable one in the hospital’s payment settings, or ask the doctor to accept one that is still offered.')
                  : emptyReason === 'plan_excludes_online'
                    ? t('appointmentmodal.no_method_plan', 'This doctor accepts online payment only, and their plan does not include it, so the appointment cannot be booked.')
                    : t('appointmentmodal.no_method_accepted', 'This doctor accepts no payment method yet, so the appointment cannot be booked. Enable one in the hospital’s payment settings and in the doctor’s Payment & bank tab.')}
            </p>
          ) : (
            <p data-testid="payment-no-methods-for-timing" className="text-xs text-amber-600">
              {t('appointmentmodal.no_method_for_prepaid', 'No online payment method is available yet. Choose “After the visit”.')}
            </p>
          )}

          {!noneAccepted && (
            <p className="text-[11px] text-gray-400">
              {activeTiming === 'prepaid'
                ? t('appointmentmodal.payment_prepaid_hint', 'Paid online in advance; cash is not available for advance payment.')
                : t('appointmentmodal.payment_collected_at_clinic', 'Collected at the clinic; the doctor marks it paid on the Payments page.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
